/*
 * @providesModule RatingPromptController
 *
 */

import React from 'react';

import _ from 'lodash';
import AWSManager from 'AWSManager';
import ClientPropertyConstants from 'ClientPropertyConstants';
import FTUEController from 'FTUEController';
import GlobalEventEmitter from 'GlobalEventEmitter';
import I18n from 'INTL';
import ModalNavigationController from 'ModalNavigationController';
import NavigationManager from 'NavigationManager';
import Preferences from 'Preferences';
import RatingModal from 'RatingModal';
import SKZConstants from 'SKZConstants';
import SKZTrophy from 'SKZTrophy';
import sprintfJS from 'sprintf-js';
import SkillzModule from 'SkillzModule';

import {LoyaltyStoreItemDetail} from 'SKZLoyalty';

import {Alert, AppState, Linking, NativeModules, Platform} from 'react-native';

const SKZAppRatingController = NativeModules.SKZAppRatingController;
const sprintf = sprintfJS.sprintf;

class RatingPromptController {
  constructor() {
    if (this.shouldUseOTARatingPrompt()) {
      console.debug('RatingPromptController: should use JS rating prompt');
      this._resetState();
      this._addListeners();
      this._applicationLaunched();
    }
    this.appStateChangeListener = null;
  }

  _resetState() {
    console.debug('RatingPromptController: resetting state');
    this._removeListeners();
    this.ratingModalHasPresented = false;
    this.subscriptions = [];
    this.matchLosses = 0;
    this.matchWins = 0;
    if (this.appStateChangeListener) {
      this.appStateChangeListener.remove();
    }
  }

  _addListeners() {
    console.debug('RatingPromptController: adding listeners');
    this.appStateChangeListener = AppState.addEventListener('change', this._handleAppStateChange);
    this.subscriptions.push(
      GlobalEventEmitter.addListener('TrophyClaimed', (trophy: string) => {
        this._handleNativeTrophyClaimed(trophy);
      })
    );
  }

  _removeListeners() {
    if (this.subscriptions) {
      this.subscriptions.forEach((listener: unknown) => {
        if (listener) {
          listener.remove();
        }
      });
      this.subscriptions = [];
    }
  }

  _displayAvailableForRatingPrompt(): boolean {
    return NavigationManager.getRouteLength() <= 1 && !ModalNavigationController.isModalShown();
  }

  showRatingPromptIfPossible() {
    if (
      this._displayAvailableForRatingPrompt() &&
      this._shouldPromptForRating() &&
      FTUEController.hasSeenMatchStreamTutorial() &&
      !this.ratingModalHasPresented &&
      !ClientPropertyConstants.getMigrateUserEnabled()
    ) {
      this.ratingModalHasPresented = true;
      this._trackAWSEvent('RATING_PROMPT_DISPLAYED', -1); // eslint-disable-line no-magic-numbers
      this._showRatingModal();
    }
  }

  _handleNativeTrophyClaimed(trophy: string) {
    if (trophy && trophy.length > 1) {
      const trophyObj = new SKZTrophy(JSON.parse(trophy));

      if (this._shouldTriggerForTrophy(trophyObj)) {
        this._trophyClaimedEvent(trophyObj);
      }
    }
  }

  _shouldTriggerForTrophy(trophy: SKZTrophy): boolean {
    const prizeTotal = trophy.getPrizeTotal();
    const trophyId = trophy.id || '';
    const trophyIdList = ClientPropertyConstants.getRatingPromptTrophyIds();

    if (trophy.isCashAward()) {
      if (ClientPropertyConstants.getRatingPromptCashTrophyThreshold() < 0) {
        const index = _.findIndex(trophyIdList, (id: number): boolean => {
          return id.toString() === trophyId.toString();
        });
        if (index !== -1) {
          // eslint-disable-line no-magic-numbers
          console.debug('RatingPromptController: logging trophy claimed event for cash trophy id');
          return true;
        }
        return false;
      } else if (prizeTotal >= ClientPropertyConstants.getRatingPromptCashTrophyThreshold()) {
        console.debug(
          'RatingPromptController: logging trophy claimed event for cash trophy threshold'
        );
        return true;
      }
      return false;
    } else if (trophy.isTicketzAward()) {
      if (ClientPropertyConstants.getRatingPromptTicketzTrophyThreshold() < 0) {
        const index = _.findIndex(trophyIdList, (id: number): boolean => {
          return id.toString() === trophyId.toString();
        });
        if (index !== -1) {
          // eslint-disable-line no-magic-numbers
          console.debug(
            'RatingPromptController: logging trophy claimed event for ticketz trophy id'
          );
          return true;
        }
        return false;
      } else if (prizeTotal >= ClientPropertyConstants.getRatingPromptTicketzTrophyThreshold()) {
        console.debug(
          'RatingPromptController: logging trophy claimed event for ticketz trophy threshold'
        );
        return true;
      }
      return false;
    } else {
      if (ClientPropertyConstants.getRatingPromptZTrophyThreshold() < 0) {
        const index = _.findIndex(trophyIdList, (id: number): boolean => {
          return id.toString() === trophyId.toString();
        });
        if (index !== -1) {
          // eslint-disable-line no-magic-numbers
          console.debug(
            'RatingPromptController: logging trophy claimed event for non cash trophy id'
          );
          return true;
        }
        return false;
      } else if (prizeTotal >= ClientPropertyConstants.getRatingPromptZTrophyThreshold()) {
        console.debug(
          'RatingPromptController: logging trophy claimed event for non cash trophy threshold'
        );
        return true;
      }
      return false;
    }
  }

  _trophyClaimedEvent(trophy: SKZTrophy) {
    if (this.shouldUseOTARatingPrompt()) {
      this._logEvent(false);
    } else {
      SKZAppRatingController.triggerEvent(false);
    }

    const trophyId = trophy.id || '';
    this._trackAWSEvent('RATING_PROMPT_TRIGGER_TROPHY', trophyId);
  }

  _logEvent(canShowPrompt: boolean) {
    this._incrementEventCount();
    console.debug('RatingPromptController: logging event, can show prompt after: ' + canShowPrompt);
    if (canShowPrompt) {
      this.showRatingPromptIfPossible();
    }
  }

  _showRatingModal() {
    const modalDisplayProps = {
      animationType: 'fade',
      transparent: true,
      modalBackHandler: (): boolean => {
        return false;
      },
    };

    ModalNavigationController.showModal(<RatingModal />, modalDisplayProps);
  }

  _handleAppStateChange = (nextAppState: unknown) => {
    if (nextAppState === 'active') {
      console.debug('RatingPromptController: app state changed to active, increment use count');
      this._incrementUseCount();
    }
  };

  _applicationLaunched() {
    console.debug('RatingPromptController: app launched increment use count');
    const lastUsedVersion = Preferences.get(SKZConstants.SKZ_RATING_PROMPT_LAST_VERSION_USED);
    const firstUsedDate = this._firstUsedDate();
    const appVersion = _.get(SkillzModule, 'App.AppVersion', '') + '';

    console.debug(`AppVersion: ${appVersion} lastUsedVersion: ${lastUsedVersion}`);
    if (!firstUsedDate || lastUsedVersion != appVersion) {
      console.debug('RatingPromptController: app launched updating last version used');
      Preferences.set({[SKZConstants.SKZ_RATING_PROMPT_LAST_VERSION_USED]: appVersion});
      this._updateAppForNewVersion();
    }

    this._incrementUseCount();
  }

  _updateAppForNewVersion() {
    const firstUsedDate = this._firstUsedDate();
    const nowDate = new Date(Date.now());
    const daysUntil = this._getDaysUntilPrompt() - 1;

    if (!firstUsedDate || this._ratedAnyVersion()) {
      console.debug('RatingPromptController: app launched reset defaults');
      this._setFirstUsedDate(0);
      Preferences.set({[SKZConstants.SKZ_RATING_PROMPT_USE_COUNT]: 0});
      Preferences.set({[SKZConstants.SKZ_RATING_PROMPT_EVENT_COUNT]: 0});
      Preferences.set({[SKZConstants.SKZ_RATING_PROMPT_LAST_REMINDED]: ''});
    } else if (nowDate.getTime() - firstUsedDate.getTime() > daysUntil * SKZConstants.MS_PER_DAY) {
      console.debug('RatingPromptController: app launched previously installed but no prompt yet');
      const rateTime = daysUntil * (-1 * SKZConstants.MS_PER_DAY); // eslint-disable-line no-magic-numbers
      this._setFirstUsedDate(rateTime);
    }
  }

  _shouldPromptForRating(): boolean {
    const nowDate = new Date(Date.now());
    const firstUsedDate = this._firstUsedDate() || nowDate;
    const firstUsedInterval = nowDate.getTime() - firstUsedDate.getTime();
    const lastRemindDate = this._lastRemindedDate() || nowDate;
    const lastRemindInterval = nowDate.getTime() - lastRemindDate.getTime();
    const useCount = Preferences.get(SKZConstants.SKZ_RATING_PROMPT_USE_COUNT) || 0;
    const eventCount = Preferences.get(SKZConstants.SKZ_RATING_PROMPT_EVENT_COUNT) || 0;
    const prefsInstallMillis =
      ClientPropertyConstants.getRatingPromptInstallSeconds() * SKZConstants.MS_PER_SEC;
    const prefsRemindMillis =
      ClientPropertyConstants.getRatingPromptRemindSeconds() * SKZConstants.MS_PER_SEC;

    if (this._ratedThisVersion()) {
      console.debug('RatingPromptController: shouldPrompt: no, already prompted this version');
      return false;
    } else if (this._declinedAnyVersion()) {
      console.debug('RatingPromptController: shouldPrompt: no, user has declined to rate the app');
      return false;
    } else if (firstUsedInterval < prefsInstallMillis) {
      const installTime = prefsInstallMillis / SKZConstants.MS_PER_DAY;
      console.debug(
        'RatingPromptController: shouldPrompt: no, because the app was first used less than ' +
          installTime +
          ' days ago'
      );
      return false;
    } else if (
      useCount < ClientPropertyConstants.getRatingPromptSessionsPerPrompt() ||
      eventCount < ClientPropertyConstants.getRatingPromptEventsRequired()
    ) {
      console.debug(
        'RatingPromptController: shouldPrompt: no, app has only been used ' +
          useCount +
          ' times or ' +
          eventCount +
          ' events have been logged'
      );
      return false;
    } else if (
      this._getUsesPerWeek() < ClientPropertyConstants.getRatingPromptWeeklySessionsPerPrompt()
    ) {
      console.debug(
        'RatingPromptController: shouldPrompt: no, app has only been used ' +
          this._getUsesPerWeek() +
          ' times per week on average since it was installed'
      );
      return false;
    } else if (this._lastRemindedDate() && lastRemindInterval < prefsRemindMillis) {
      const remindDays = prefsRemindMillis / SKZConstants.MS_PER_DAY;
      console.debug(
        'RatingPromptController: shouldPrompt: no, user last asked to be reminded less than ' +
          remindDays +
          ' days ago'
      );
      return false;
    }

    console.debug('RatingPromptController: shouldPrompt: yes, all conditions have been met!');
    return true;
  }

  _firstUsedDate(): Date {
    const firstUsed = Preferences.get(SKZConstants.SKZ_RATING_PROMPT_FIRST_USED);
    const firstUsedDate = firstUsed ? new Date(firstUsed) : null;
    return firstUsedDate;
  }

  _setFirstUsedDate(additionalTime: number) {
    const dateNow = new Date(Date.now());
    const dateNowTime = dateNow.getTime();
    const newRateDate = new Date(dateNowTime + additionalTime);
    Preferences.set({[SKZConstants.SKZ_RATING_PROMPT_FIRST_USED]: newRateDate.toISOString()});
  }

  _getUsesPerWeek(): number {
    const firstUsed = this._firstUsedDate();
    const now = new Date(Date.now());
    const firstUsedMillis = firstUsed ? firstUsed.getTime() : now.getTime();
    const nowMillis = now.getTime();
    const usesCount = Preferences.get(SKZConstants.SKZ_RATING_PROMPT_USE_COUNT) || 0;
    return usesCount / ((nowMillis - firstUsedMillis) / (SKZConstants.MS_PER_DAY * 7)); // eslint-disable-line no-magic-numbers
  }

  _setRatedThisVersion() {
    const appVersion = _.get(SkillzModule, 'App.AppVersion', '') + '';
    Preferences.set({[SKZConstants.SKZ_RATING_PROMPT_RATED_VERSION]: appVersion});
  }

  _ratedThisVersion(): boolean {
    const lastRatedVersion = Preferences.get(SKZConstants.SKZ_RATING_PROMPT_RATED_VERSION) || '';
    const currentVersion = _.get(SkillzModule, 'App.AppVersion', '') + '';
    return lastRatedVersion == currentVersion;
  }

  _ratedAnyVersion(): boolean {
    const lastRatedVersion = Preferences.get(SKZConstants.SKZ_RATING_PROMPT_RATED_VERSION);
    return lastRatedVersion && lastRatedVersion != undefined && lastRatedVersion.length != 0;
  }

  _setDeclinedThisVersion() {
    const appVersion = _.get(SkillzModule, 'App.AppVersion', '') + '';
    Preferences.set({[SKZConstants.SKZ_RATING_PROMPT_DECLINED_VERSION]: appVersion});
  }

  _declinedThisVersion(): boolean {
    const lastDeclinedVersion =
      Preferences.get(SKZConstants.SKZ_RATING_PROMPT_DECLINED_VERSION) || '';
    const currentVersion = _.get(SkillzModule, 'App.AppVersion', '') + '';
    return lastDeclinedVersion == currentVersion;
  }

  _declinedAnyVersion(): boolean {
    const lastDeclinedVersion = Preferences.get(SKZConstants.SKZ_RATING_PROMPT_DECLINED_VERSION);
    return (
      lastDeclinedVersion && lastDeclinedVersion != undefined && lastDeclinedVersion.length != 0
    );
  }

  _lastRemindedDate(): Date {
    const lastRemindedStr = Preferences.get(SKZConstants.SKZ_RATING_PROMPT_LAST_REMINDED);
    const lastRemindedDate = lastRemindedStr ? new Date(lastRemindedStr) : null;
    return lastRemindedDate;
  }

  _setLastRemindedDate() {
    const dateNow = new Date(Date.now());
    Preferences.set({[SKZConstants.SKZ_RATING_PROMPT_LAST_REMINDED]: dateNow.toISOString()});
  }

  _incrementUseCount() {
    const useCount: number = Preferences.get(SKZConstants.SKZ_RATING_PROMPT_USE_COUNT) ?? 0;

    Preferences.set({[SKZConstants.SKZ_RATING_PROMPT_USE_COUNT]: useCount + 1});
  }

  _incrementEventCount() {
    const eventCount = Preferences.get(SKZConstants.SKZ_RATING_PROMPT_EVENT_COUNT) || 0;

    Preferences.set({[SKZConstants.SKZ_RATING_PROMPT_EVENT_COUNT]: eventCount + 1});
  }

  _getDaysUntilPrompt(): number {
    const generalInstallSeconds = ClientPropertyConstants.getRatingPromptInstallSeconds();
    return generalInstallSeconds / SKZConstants.SECONDS_PER_DAY;
  }

  _getRemindPeriod(): number {
    const remindSeconds = ClientPropertyConstants.getRatingPromptRemindSeconds();
    return remindSeconds / SKZConstants.SECONDS_PER_DAY;
  }

  _getDeepLinkToAppStore(completion: () => {}) {
    if (completion) {
      if (Platform.OS == 'android') {
        const bundleID = _.get(SkillzModule, 'App.PackageId', '');
        const samsungURL =
          bundleID.length > 0 ? SKZConstants.SKZ_RATING_PROMPT_SAMSUNG_STORE_FORMAT + bundleID : '';
        completion(samsungURL);
      } else {
        SkillzModule.getAppStoreID((appID: string) => {
          const iOSURL =
            appID.length > 0
              ? sprintf(SKZConstants.SKZ_RATING_PROMPT_IOS_STORE_RATE_FORMAT, appID)
              : '';
          completion(iOSURL);
        });
      }
    }
  }

  _deepLinkToAppStore() {
    this._getDeepLinkToAppStore((url: string) => {
      Linking.canOpenURL(url)
        .then((supported: boolean): Object => {
          if (supported && url.length > 0) {
            Linking.openURL(url);
          }
        })
        .catch((error: unknown): void =>
          console.error('RatingPromptController: An error occured', error)
        );
    });
  }

  _trackAWSEvent(name: string, id: number) {
    const params = {id: id};
    AWSManager.trackEvent(name, params);
  }

  _onRateNow() {
    this._setRatedThisVersion();
    this._trackAWSEvent('RATING_PROMPT_RESPONSE_RATENOW', -1); // eslint-disable-line no-magic-numbers
    this._deepLinkToAppStore();
  }

  _onRemindLater(logEvent: boolean) {
    if (logEvent) {
      this._trackAWSEvent('RATING_PROMPT_RESPONSE_IDWTH', -1); // eslint-disable-line no-magic-numbers
    }
    this._setLastRemindedDate();
  }

  _checkDisplayAfterWin(completion: (canDisplay: boolean) => {}) {
    this.matchWins += 1;
    const winRate = this.matchLosses > 0 ? this.matchWins / this.matchLosses : this.matchWins;
    const firstUsed = this._firstUsedDate();
    const now = new Date(Date.now());
    const firstUsedMillis = firstUsed ? firstUsed.getTime() : now.getTime();
    const secondsBetween = (now.getTime() - firstUsedMillis) / SKZConstants.MS_PER_SEC;
    const canDisplayIgnoringSession =
      winRate >= ClientPropertyConstants.getRatingPromptAfterWinWinRate() &&
      secondsBetween >= ClientPropertyConstants.getRatingPromptAfterWinInstallTime();

    const methodExists = typeof SkillzModule.getSessionLengthInMillis === 'function';
    if (methodExists) {
      SkillzModule.getSessionLengthInMillis((sessionTime: number) => {
        const sessionSeconds = sessionTime / SKZConstants.MS_PER_SEC;

        completion(
          canDisplayIgnoringSession &&
            sessionSeconds >= ClientPropertyConstants.getRatingPromptAfterWinSessionSeconds()
        );
      });
    } else {
      console.debug(
        'RatingPromptController: not accounting for session time when determining if should log event after win'
      );
      completion(canDisplayIgnoringSession);
    }
  }

  _recordMatchResult(didWinPrize: boolean, id: number) {
    console.debug('RatingPromptController: recording match result for match id: ' + id);
    if (didWinPrize) {
      this._checkDisplayAfterWin((canDisplay: boolean) => {
        if (canDisplay) {
          console.debug(
            'RatingPromptController: recording match result for match id: ' + id + ', log event'
          );
          this._trackAWSEvent('RATING_PROMPT_TRIGGER_WIN', id);
          this._logEvent(false);
        }
      });
    } else {
      this.matchLosses += 1;
    }
  }

  _shouldTriggerRatingModalForTicketz(ticketzReward: Object): boolean {
    console.debug(
      'RatingPromptController: recording ticketz reward for ticketz id: ' + ticketzReward.id
    );
    const ticketzThreshold = ClientPropertyConstants.getRatingPromptTicketzThreshold();
    const ticketzIds = ClientPropertyConstants.getRatingPromptTicketzRewardIds();
    const ticketzRewardId = ticketzReward.id;
    const index = _.findIndex(ticketzIds, (id: unknown): boolean => {
      return id.toString() == ticketzRewardId.toString();
    });

    if (index != -1) {
      // eslint-disable-line no-magic-numbers
      console.debug(
        'RatingPromptController: recording ticketz reward for ticketz id: ' +
          ticketzReward.id +
          ', log event for id'
      );
      return true;
    } else if (ticketzReward.ticketz_cost >= ticketzThreshold && ticketzThreshold >= 0) {
      console.debug(
        'RatingPromptController: recording ticketz reward for ticketz id: ' +
          ticketzReward.id +
          ', log event for threshold'
      );
      return true;
    }

    return false;
  }

  _shouldTriggerRatingModalForLoyalty(loyaltyReward: LoyaltyStoreItemDetail): boolean {
    console.debug('RatingPromptController: recording loyalty reward for sku: ' + loyaltyReward.sku);
    const ticketzThreshold = ClientPropertyConstants.getRatingPromptTicketzThreshold();

    if (loyaltyReward.ticketz_price >= ticketzThreshold && ticketzThreshold >= 0) {
      console.debug(
        'RatingPromptController: recording loyalty reward for sku: ' +
          loyaltyReward.sku +
          ', log event for threshold'
      );
      return true;
    }

    return false;
  }

  _displayAppStorePrompt() {
    Alert.alert(
      I18n.message('Thanks! Please rate us on the app store'),
      I18n.message('Help us get you more competitors'),
      [
        {
          text: I18n.message('Rate now'),
          onPress: () => {
            this._onRateNow();
          },
        },
        {
          text: I18n.message("I don't want to help"),
          onPress: () => {
            this._onRemindLater(true);
          },
        },
      ],
      {cancelable: false}
    );
  }

  shouldUseOTARatingPrompt(): boolean {
    if (Platform.OS == 'android') {
      return true;
    } else {
      const methodExists = typeof SkillzModule.getAppStoreID === 'function';
      return SkillzModule.App.AppVersion && methodExists;
    }
  }

  recordTicketzRewardForRatingModal(ticketzReward: Object) {
    if (this._shouldTriggerRatingModalForTicketz(ticketzReward)) {
      this._trackAWSEvent('RATING_PROMPT_TRIGGER_TICKETZ', ticketzReward.id);

      if (this.shouldUseOTARatingPrompt()) {
        this._logEvent(false);
      } else {
        SKZAppRatingController.triggerEvent(false);
      }
    }
  }

  recordLoyaltyRewardForRatingModal(loyaltyReward: LoyaltyStoreItemDetail) {
    if (this._shouldTriggerRatingModalForLoyalty(loyaltyReward)) {
      this._trackAWSEvent('RATING_PROMPT_TRIGGER_TICKETZ', loyaltyReward.sku);

      if (this.shouldUseOTARatingPrompt()) {
        this._logEvent(true);
      } else {
        SKZAppRatingController.triggerEvent(false);
      }
    }
  }

  recordTrophyRewardForRatingModal(trophy: Object) {
    if (this._shouldTriggerForTrophy(trophy)) {
      this._trophyClaimedEvent(trophy);
    }
  }

  recordMatchResultForRatingTrigger(didWinPrize: boolean, id: number) {
    if (this.shouldUseOTARatingPrompt()) {
      this._recordMatchResult(didWinPrize, id);
    } else {
      SKZAppRatingController.recordMatchResultForRatingTrigger(didWinPrize, id);
    }
  }

  remindLater(logEvent: boolean = false) {
    if (this.shouldUseOTARatingPrompt()) {
      this._onRemindLater(logEvent);
    } else {
      SKZAppRatingController.remindLater();
    }
  }

  promptUserNextVerison() {
    if (this.shouldUseOTARatingPrompt()) {
      this._setRatedThisVersion();
    } else {
      SKZAppRatingController.promptUserNextVersion();
    }
  }

  showAppStoreRatingPrompt() {
    if (this.shouldUseOTARatingPrompt()) {
      this._getDeepLinkToAppStore((url: string) => {
        Linking.canOpenURL(url)
          .then((supported: boolean): Object => {
            if (supported && url.length > 0) {
              this._displayAppStorePrompt();
            } else {
              this._setRatedThisVersion();
            }
          })
          .catch((error: unknown): void =>
            console.error('RatingPromptController: error on deep link url', error)
          );
      });
    } else {
      SKZAppRatingController.iRatePromptTrigger();
    }
  }

  didDismissRatingModal() {
    this.ratingModalHasPresented = false;
  }
}

const RatingPromptControllerInstance = new RatingPromptController();

export default RatingPromptControllerInstance;
